import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';

/**
 * The page a payer lands on from an emailed link.
 *
 * Deliberately outside the console: no sidebar, no session, no navigation to
 * anywhere else in the product. Someone confirming one invoice should not be
 * shown an application they have no account for.
 */

interface PublicDealView {
  reference: string;
  type: 'INVOICE' | 'ORDER';
  currency: string;
  faceFormatted: string;
  faceMinor: string;
  issueDate: string;
  dueDate: string;
  terms: string;
  supplierName: string;
  payerName: string;
  recipientName: string | null;
  destinationHint: string;
  eInvoiceRef: string | null;
  documents: { kind: string; filename: string }[];
  actionable: boolean;
  outcome: string | null;
  expiresAt: string;
}

type Decision = 'CONFIRM' | 'CONFIRM_PARTIAL' | 'DISPUTE';

export function ConfirmPage() {
  const { token = '' } = useParams();
  const qc = useQueryClient();

  const [decision, setDecision] = useState<Decision | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  // Set only while the server is standing in for an email provider with a
  // fixed code. Nothing is disclosed by showing it — a code that never
  // changes is not a secret — and without it nobody can finish the flow,
  // because no mail is actually leaving the building.
  const [standInCode, setStandInCode] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const view = useQuery({
    queryKey: ['confirm', token],
    queryFn: () => api.get<PublicDealView>(`/confirm/${token}`, { anonymous: true }),
    retry: false,
  });

  const sendCode = useMutation({
    mutationFn: () =>
      api.post<{ destinationHint: string; staticCode?: string }>(
        `/confirm/${token}/code`,
        undefined,
        { anonymous: true },
      ),
    onSuccess: (result) => {
      setCodeSent(true);
      setError(null);
      if (result.staticCode) {
        setStandInCode(result.staticCode);
        setCode(result.staticCode);
      }
    },
    onError: (err) => setError(err),
  });

  const submit = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        code: code.trim(),
        decision,
        ...(name.trim() ? { confirmedByName: name.trim() } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      };

      if (decision === 'CONFIRM_PARTIAL') {
        // The payer types a normal amount; the wire carries minor units.
        const parsed = Number(amount.replace(/,/g, ''));
        if (!Number.isFinite(parsed) || parsed <= 0) {
          throw new ApiError(400, 'Enter the amount you accept as payable');
        }
        body['confirmedMinor'] = String(Math.round(parsed * 100));
      }

      return api.post<{ status: string; message: string }>(
        `/confirm/${token}/decide`,
        body,
        { anonymous: true },
      );
    },
    onSuccess: (result) => {
      setDone(result.message);
      setError(null);
      void qc.invalidateQueries({ queryKey: ['confirm', token] });
    },
    onError: (err) => setError(err),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!decision) return setError(new Error('Choose what you would like to do'));
    if (!code.trim()) return setError(new Error('Enter the code that was emailed to you'));
    submit.mutate();
  }

  if (view.isLoading) {
    return <Shell><p>Loading…</p></Shell>;
  }

  if (view.error) {
    return (
      <Shell>
        <h1 className="page-title" style={{ fontSize: 20 }}>
          This link is not valid
        </h1>
        <p style={{ color: 'var(--muted)' }}>
          It may have expired, already been used, or been replaced by a newer request. Contact the
          supplier who sent it and ask them to send a new one.
        </p>
      </Shell>
    );
  }

  const deal = view.data!;

  if (done) {
    return (
      <Shell>
        <div className="tag positive" style={{ marginBottom: 14 }}>
          Recorded
        </div>
        <h1 className="page-title" style={{ fontSize: 20 }}>
          {deal.reference}
        </h1>
        <p style={{ color: 'var(--muted)' }}>{done}</p>
        <p style={{ color: 'var(--muted-2)', fontSize: 12.5 }}>
          You can close this page. This link will not work again.
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="crumb">
        {deal.type === 'ORDER' ? 'Purchase order' : 'Invoice'} confirmation
      </div>
      <h1 className="page-title" style={{ fontSize: 22 }}>
        {deal.reference}
      </h1>
      <p style={{ color: 'var(--muted)', marginTop: 0 }}>
        {deal.supplierName} has asked {deal.payerName} to confirm this{' '}
        {deal.type === 'ORDER' ? 'order' : 'invoice'} is payable.
      </p>

      <dl className="dl" style={{ margin: '20px 0' }}>
        <dt>Amount</dt>
        <dd className="num" style={{ fontSize: 17, fontWeight: 700 }}>
          {deal.faceFormatted}
        </dd>

        <dt>Supplier</dt>
        <dd>{deal.supplierName}</dd>

        <dt>Issued</dt>
        <dd>{deal.issueDate}</dd>

        <dt>Due</dt>
        <dd>
          {deal.dueDate} · {deal.terms}
        </dd>

        {deal.eInvoiceRef ? (
          <>
            <dt>E-invoice reference</dt>
            <dd className="num">{deal.eInvoiceRef}</dd>
          </>
        ) : null}

        {deal.documents.length > 0 ? (
          <>
            <dt>Attached</dt>
            <dd>{deal.documents.map((d) => d.filename).join(', ')}</dd>
          </>
        ) : null}
      </dl>

      {!deal.actionable ? (
        <div className="err" style={{ background: 'var(--slate-soft)', color: 'var(--slate)' }}>
          This {deal.type === 'ORDER' ? 'order' : 'invoice'} is no longer awaiting your
          confirmation{deal.outcome ? ` — it is recorded as ${humanise(deal.outcome)}.` : '.'}
        </div>
      ) : (
        <form onSubmit={onSubmit}>
          <div className="section-title" style={{ marginTop: 0 }}>
            What would you like to do?
          </div>

          <div style={{ display: 'grid', gap: 8, marginBottom: 18 }}>
            <Choice
              checked={decision === 'CONFIRM'}
              onChange={() => setDecision('CONFIRM')}
              title={`Confirm ${deal.faceFormatted} is payable`}
              detail="The full amount is correct and will be paid on the due date."
            />
            <Choice
              checked={decision === 'CONFIRM_PARTIAL'}
              onChange={() => setDecision('CONFIRM_PARTIAL')}
              title="Confirm a reduced amount"
              detail="Part of this invoice is payable, but not all of it."
            />
            <Choice
              checked={decision === 'DISPUTE'}
              onChange={() => setDecision('DISPUTE')}
              title="Raise a query"
              detail="Something is wrong and this should not be paid as it stands."
            />
          </div>

          {decision === 'CONFIRM_PARTIAL' ? (
            <div className="field">
              <label htmlFor="amount">Amount you accept as payable ({deal.currency})</label>
              <input
                id="amount"
                inputMode="decimal"
                placeholder="e.g. 9500.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
          ) : null}

          {decision === 'CONFIRM_PARTIAL' || decision === 'DISPUTE' ? (
            <div className="field">
              <label htmlFor="reason">
                {decision === 'DISPUTE' ? 'What is the problem?' : 'Why is the amount reduced?'}
              </label>
              <input
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="This is shared with the supplier"
              />
            </div>
          ) : null}

          <div className="field">
            <label htmlFor="name">Your name</label>
            <input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={deal.recipientName ?? 'Recorded against this confirmation'}
            />
          </div>

          <div className="section-title">Confirm it is you</div>

          {!codeSent ? (
            <>
              <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 0 }}>
                We will email a short code to {deal.destinationHint}.
              </p>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={sendCode.isPending}
                onClick={() => sendCode.mutate()}
              >
                {sendCode.isPending ? 'Sending…' : 'Email me a code'}
              </button>
            </>
          ) : (
            <>
              <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 0 }}>
                {standInCode
                  ? 'Email delivery is not configured yet, so a standing code is in use. It still expires, can be used once, and locks after a few wrong tries.'
                  : `Enter the code sent to ${deal.destinationHint}. It expires shortly and can be used once.`}
              </p>
              {standInCode ? (
                <div
                  className="notice"
                  style={{
                    background: 'var(--amber-soft)',
                    color: 'var(--amber)',
                    padding: '10px 12px',
                    borderRadius: 'var(--r-sm)',
                    fontSize: 13,
                    marginBottom: 12,
                  }}
                >
                  Standing code <strong>{standInCode}</strong> — filled in below.
                </div>
              ) : null}
              <div className="field">
                <label htmlFor="code">Code</label>
                <input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className="num"
                  style={{ letterSpacing: 6, fontSize: 18, maxWidth: 200 }}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
              </div>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ marginBottom: 14 }}
                disabled={sendCode.isPending}
                onClick={() => sendCode.mutate()}
              >
                Send another code
              </button>
            </>
          )}

          {/* Rendered after the code block so a failure appears next to the
              control that caused it. */}
          <ErrorLine error={error} />

          {codeSent ? (
            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', marginTop: 6 }}
              disabled={submit.isPending}
            >
              {submit.isPending ? 'Submitting…' : 'Submit'}
            </button>
          ) : null}
        </form>
      )}

      <p style={{ color: 'var(--muted-2)', fontSize: 11.5, marginTop: 26 }}>
        This link was sent to {deal.destinationHint} and expires on{' '}
        {deal.expiresAt.slice(0, 10)}. If you were not expecting it, tell your finance team.
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="login-wrap" style={{ alignItems: 'flex-start', paddingTop: 48 }}>
      <div className="login-card" style={{ maxWidth: 540 }}>
        <div className="brand-name" style={{ fontSize: 20 }}>
          Accel
        </div>
        <div className="brand-tag" style={{ marginBottom: 20 }}>
          Secure invoice confirmation
        </div>
        {children}
      </div>
    </div>
  );
}

function Choice({
  checked,
  onChange,
  title,
  detail,
}: {
  checked: boolean;
  onChange: () => void;
  title: string;
  detail: string;
}) {
  return (
    <label
      style={{
        display: 'flex',
        gap: 10,
        padding: '11px 13px',
        border: `1px solid ${checked ? 'var(--blue)' : 'var(--line)'}`,
        background: checked ? 'var(--blue-soft)' : 'var(--card)',
        borderRadius: 'var(--r-sm)',
        cursor: 'pointer',
      }}
    >
      <input
        type="radio"
        name="decision"
        checked={checked}
        onChange={onChange}
        style={{ marginTop: 3 }}
      />
      <span>
        <span style={{ fontWeight: 600, display: 'block', fontSize: 13.5 }}>{title}</span>
        <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>{detail}</span>
      </span>
    </label>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  return <div className="err">{message}</div>;
}

function humanise(value: string): string {
  return value.replace(/_/g, ' ').toLowerCase();
}
